import type {
  PrintableAreaConfig,
  ProductConfig,
  ProductSizeProfile,
  ProductVariant,
  ProductViewId,
} from '../domain/types';

export interface ResolvedGarmentSelection {
  variant: ProductVariant;
  profile: ProductSizeProfile;
}

export interface GarmentVisualScale {
  x: number;
  y: number;
}

export function resolveProductVariant(
  product: ProductConfig,
  variantId?: string,
): ProductVariant | undefined {
  return product.variants.find(
    variant => variant.id === variantId || variant.legacyIds?.includes(variantId ?? ''),
  );
}

/** Sizes currently offered to customers; all configured sizes still retain their profiles. */
export function resolveSelectableSizes(variant: ProductVariant): string[] {
  return variant.selectableSizes ?? variant.sizes;
}

export function isSizeSelectable(variant: ProductVariant, size: string): boolean {
  return resolveSelectableSizes(variant).includes(size);
}

export function resolveDefaultSize(variant: ProductVariant): string {
  const selectableSizes = resolveSelectableSizes(variant);
  if (variant.defaultSize && selectableSizes.includes(variant.defaultSize)) {
    return variant.defaultSize;
  }
  return selectableSizes.includes('M') ? 'M' : (selectableSizes[0] ?? '');
}

/** Resolve an exact physical profile. Invalid persisted selections fail loudly. */
export function resolveSizeProfile(
  product: ProductConfig,
  variantId: string,
  size: string,
): ProductSizeProfile {
  const variant = resolveProductVariant(product, variantId);
  const profile = variant?.sizeProfiles?.find(candidate => candidate.size === size);
  if (!profile) {
    throw new Error(`Product ${product.id} has no size profile for ${variantId}/${size}`);
  }
  return profile;
}

export function resolveGarmentSelection(
  product: ProductConfig,
  variantId: string,
  size: string,
): ResolvedGarmentSelection {
  const variant = resolveProductVariant(product, variantId);
  if (!variant || !isSizeSelectable(variant, size)) {
    throw new Error(`Product ${product.id} has no garment selection ${variantId}/${size}`);
  }
  return { variant, profile: resolveSizeProfile(product, variantId, size) };
}

/** Physical preview scale shared by the editor, summary and generated customer documents. */
export function resolveGarmentVisualScale(
  product: ProductConfig,
  profile: ProductSizeProfile,
): GarmentVisualScale {
  const referenceVariant =
    product.variants.find(item => item.id === 'unisex') ?? product.variants[0]!;
  const referenceSizes = resolveSelectableSizes(referenceVariant);
  const referenceSize = referenceSizes.includes('M')
    ? 'M'
    : referenceSizes[Math.floor(referenceSizes.length / 2)]!;
  const reference = resolveSizeProfile(product, referenceVariant.id, referenceSize);
  return {
    x: clampScale(profile.garmentMm.flatWidth / reference.garmentMm.flatWidth, 0.5, 1.22),
    y: clampScale(profile.garmentMm.bodyLength / reference.garmentMm.bodyLength, 0.5, 1.14),
  };
}

function clampScale(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

/**
 * Resolve a size-aware torso placement canvas. An approved press/transfer limit can
 * be configured per size later; until then the placement canvas is the only hard bound.
 */
export function resolvePrintableArea(
  product: ProductConfig,
  variantId: string,
  size: string,
  viewId: ProductViewId,
): PrintableAreaConfig {
  const base = product.printableAreas.find(area => area.viewId === viewId);
  if (!base) throw new Error(`Product ${product.id} has no printable area for ${viewId}`);
  const variant = resolveProductVariant(product, variantId);
  const variantArea = {
    ...structuredClone(base),
    mockupImage: variant?.mockupImages?.[viewId] ?? base.mockupImage,
  };
  if (!variant?.sizeProfiles) return variantArea;

  const geometry = resolveSizeProfile(product, variantId, size).surfaces[viewId];
  if (!geometry) {
    throw new Error(`Product ${product.id} has no ${viewId} geometry for ${variantId}/${size}`);
  }
  const previewScale = variant.previewAreaScale;
  const previewWidthPct = base.previewArea.widthPct * (previewScale?.x ?? 1);
  const previewHeightPct = base.previewArea.heightPct * (previewScale?.y ?? 1);
  return {
    ...variantArea,
    sizeMm: structuredClone(geometry.placementAreaMm),
    maxPrintSizeMm: structuredClone(geometry.maxTransferSizeMm ?? geometry.placementAreaMm),
    previewArea: {
      xPct: base.previewArea.xPct + (base.previewArea.widthPct - previewWidthPct) / 2,
      yPct: base.previewArea.yPct + (base.previewArea.heightPct - previewHeightPct) / 2,
      widthPct: previewWidthPct,
      heightPct: previewHeightPct,
    },
  };
}
